import { create } from "zustand";

type BulkSelectionState = {
  selectionEnabled: boolean;
  selectAllAcrossPages: boolean;
  selectedRowIds: Set<string>;
  deselectedRowIds: Set<string>;
  totalRowCount: number;
};

type BulkSelectionActions = {
  setSelectionEnabled: (enabled: boolean) => void;
  setTotalRowCount: (count: number) => void;
  resetSelection: () => void;
  toggleRow: (
    rowId: string,
    isSelected: boolean,
    currentPageRowIds: string[],
  ) => void;
  togglePage: (pageRowIds: string[], isSelected: boolean) => void;
  selectAllAcrossFiltered: () => void;
  isRowSelected: (rowId: string) => boolean;
  getCurrentPageSelectedCount: (pageRowIds: string[]) => number;
  getTotalSelectedCount: () => number;
  getHeaderCheckboxState: (
    pageRowIds: string[],
  ) => "checked" | "unchecked" | "indeterminate";
};

const initialState = (): BulkSelectionState => ({
  selectionEnabled: false,
  selectAllAcrossPages: false,
  selectedRowIds: new Set<string>(),
  deselectedRowIds: new Set<string>(),
  totalRowCount: 0,
});

export const useBulkSelectionStore = create<
  BulkSelectionState & BulkSelectionActions
>((set, get) => ({
  ...initialState(),

  setSelectionEnabled: (enabled) =>
    set((state) => {
      if (!enabled) {
        return {
          ...initialState(),
          selectionEnabled: false,
          totalRowCount: state.totalRowCount,
        };
      }
      return { ...state, selectionEnabled: true };
    }),

  setTotalRowCount: (count) =>
    set((state) => ({
      ...state,
      totalRowCount: Math.max(0, count),
    })),

  resetSelection: () =>
    set((state) => ({
      ...state,
      selectAllAcrossPages: false,
      selectedRowIds: new Set(),
      deselectedRowIds: new Set(),
    })),

  toggleRow: (rowId, isSelected, currentPageRowIds: string[]) =>
    set((state) => {
      if (!state.selectionEnabled) {
        return state;
      }

      // Normal mode: track explicitly selected rows
      if (!state.selectAllAcrossPages) {
        const newSelected = new Set(state.selectedRowIds);

        if (isSelected) {
          newSelected.add(rowId);
        } else {
          newSelected.delete(rowId);
        }

        return { ...state, selectedRowIds: newSelected };
      }

      // "Select all" mode: if deselecting a row => normal mode for the current page
      if (!isSelected) {
        const newSelected = new Set(
          currentPageRowIds?.filter(
            (id) => id !== rowId && !state.deselectedRowIds.has(id),
          ) || [],
        );

        return {
          ...state,
          selectAllAcrossPages: false,
          selectedRowIds: newSelected,
          deselectedRowIds: new Set(),
        };
      }

      return state;
    }),

  togglePage: (pageRowIds, isSelected) =>
    set((state) => {
      if (!state.selectionEnabled) {
        return state;
      }

      if (!state.selectAllAcrossPages) {
        const newSelected = new Set(state.selectedRowIds);

        for (const id of pageRowIds) {
          if (isSelected) {
            newSelected.add(id);
          } else {
            newSelected.delete(id);
          }
        }

        return { ...state, selectedRowIds: newSelected };
      }

      if (!isSelected) {
        return {
          ...state,
          selectAllAcrossPages: false,
          selectedRowIds: new Set(),
          deselectedRowIds: new Set(),
        };
      }
      return state;
    }),

  selectAllAcrossFiltered: () =>
    set((state) => ({
      ...state,
      selectAllAcrossPages: true,
      selectedRowIds: new Set(), // Clear because everything is now selected by default
    })),

  isRowSelected: (rowId) => {
    const state = get();

    if (!state.selectionEnabled) {
      return false;
    }

    if (state.selectAllAcrossPages) {
      // In "select all" mode, a row is selected unless it's in the exceptions
      return !state.deselectedRowIds.has(rowId);
    }

    // In normal mode, a row is selected if it's in the list
    return state.selectedRowIds.has(rowId);
  },

  getCurrentPageSelectedCount: (pageRowIds) => {
    const state = get();

    if (!state.selectionEnabled || pageRowIds.length === 0) {
      return 0;
    }

    let count = 0;

    if (!state.selectAllAcrossPages) {
      // Count how many page rows are in selectedRowIds
      for (const id of pageRowIds) {
        if (state.selectedRowIds.has(id)) {
          count++;
        }
      }
    } else {
      // Count how many page rows are NOT in deselectedRowIds
      for (const id of pageRowIds) {
        if (!state.deselectedRowIds.has(id)) {
          count++;
        }
      }
    }

    return count;
  },

  getTotalSelectedCount: () => {
    const state = get();

    if (!state.selectionEnabled) {
      return 0;
    }

    if (!state.selectAllAcrossPages) {
      // Normal mode: number of explicitly selected rows
      const count = state.selectedRowIds.size;
      return Math.min(count, state.totalRowCount);
    }

    // "Select all" mode: total minus exceptions
    const count = state.totalRowCount - state.deselectedRowIds.size;
    return Math.max(0, Math.min(count, state.totalRowCount));
  },

  getHeaderCheckboxState: (pageRowIds) => {
    const state = get();

    if (!state.selectionEnabled || pageRowIds.length === 0) {
      return "unchecked";
    }

    const selectedCount = state.getCurrentPageSelectedCount(pageRowIds);

    if (selectedCount === 0) {
      return "unchecked";
    }

    if (selectedCount === pageRowIds.length) {
      return "checked";
    }

    return "indeterminate";
  },
}));
